import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { FaBox, FaCheckCircle, FaExclamationTriangle, FaShoppingCart, FaTimes, FaPrint } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 1.75rem;
    gap: 0.75rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;

  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const ViewSelectedButton = styled.button`
  padding: 0.6rem 1rem;
  background: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;
  font-size: 0.85rem;

  &:hover {
    background: #1976D2;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const SelectedCount = styled.span`
  background: white;
  color: #2196F3;
  padding: 0.15rem 0.4rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;

  @media (min-width: 768px) {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.75rem;
  border-radius: 8px;
  border-left: 4px solid ${props => props.$color || '#dc3545'};
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.65rem;
  color: #666;
  margin-bottom: 0.25rem;

  @media (min-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    gap: 1.5rem;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 2px solid ${props => props.$selected ? '#2196F3' : '#e0e0e0'};
  transition: border-color 0.2s;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const ProductHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: auto 1fr 200px auto;
    gap: 1rem;
    align-items: center;
  }
`;

const MobileHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (min-width: 768px) {
    display: contents;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.h3`
  margin: 0 0 0.25rem 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;

  @media (min-width: 768px) {
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
  }
`;

const ProductType = styled.div`
  font-size: 0.75rem;
  color: #666;

  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const MobileActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: stretch;

  @media (min-width: 768px) {
    display: contents;
  }
`;

const OrderInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;

  @media (min-width: 768px) {
    flex: none;
    width: 200px;
  }
`;

const InputLabel = styled.label`
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
`;

const OrderInput = styled.input`
  padding: 0.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.95rem;
  width: 100%;
  text-align: center;
  font-weight: 600;

  &:focus {
    outline: none;
    border-color: #2196F3;
  }
`;

const ProductActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const MarkReceivedButton = styled.button`
  padding: 0.6rem 1rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.2s;
  white-space: nowrap;
  font-size: 0.85rem;

  &:hover {
    background: #218838;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (min-width: 768px) {
    gap: 0.75rem;
  }
`;

const OrderItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 100px 1fr auto 120px 100px;
    gap: 1rem;
    align-items: center;
  }
`;

const OrderItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    display: contents;
  }
`;

const OrderNumber = styled.div`
  font-weight: 600;
  color: #007bff;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const OrderClient = styled.div`
  color: #666;
  font-size: 0.8rem;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 0.875rem;
    white-space: normal;
  }
`;

const OrderDimensions = styled.div`
  font-size: 0.8rem;
  color: #666;
  font-family: monospace;

  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const OrderQuantity = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const OrderUnit = styled.div`
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
  padding: 0.2rem 0.4rem;
  background: #e3f2fd;
  border-radius: 4px;

  @media (min-width: 768px) {
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #28a745;
`;

const EmptyTitle = styled.h2`
  color: #333;
  margin-bottom: 0.5rem;
`;

const EmptyText = styled.p`
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 1rem;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;

  @media (min-width: 768px) {
    border-radius: 8px;
    padding: 1.5rem;
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const PrintButton = styled.button`
  padding: 0.5rem 1rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;

  &:hover {
    background: #45a049;
  }

  @media print {
    display: none;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const CompactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media print {
    gap: 0.25rem;
  }
`;

const CompactItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #2196F3;

  @media print {
    padding: 0.5rem;
    background: white;
    border: 1px solid #ddd;
    page-break-inside: avoid;
  }
`;

const CompactName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;

  @media print {
    font-size: 11pt;
  }
`;

const CompactQuantity = styled.div`
  color: #2196F3;
  font-size: 0.95rem;
  font-weight: 700;
  text-align: right;

  @media print {
    font-size: 11pt;
    color: #000;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  margin-top: 0.75rem;
  background: #e3f2fd;
  border-radius: 4px;
  font-weight: 700;
  font-size: 1rem;

  @media print {
    background: white;
    border-top: 2px solid #000;
    margin-top: 1rem;
    padding-top: 0.5rem;
  }
`;

const PrintHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #000;

    h1 {
      margin: 0 0 0.5rem 0;
      font-size: 16pt;
    }

    p {
      margin: 0;
      font-size: 10pt;
      color: #666;
    }
  }
`;

const StockShortages = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedProducts, setSelectedProducts] = useState({});
  const [orderQuantities, setOrderQuantities] = useState({});
  const [showSelectedModal, setShowSelectedModal] = useState(false);

  // Fetch stock shortages
  const { data: shortages, isLoading, error } = useQuery({
    queryKey: ['stockShortages'],
    queryFn: async () => {
      const response = await api.get('/orders/stock-shortages');
      return response.data;
    },
  });

  // Load saved order quantities when data loads
  useEffect(() => {
    if (shortages?.products) {
      const savedQuantities = {};
      shortages.products.forEach(product => {
        if (product.orderQuantity) {
          savedQuantities[product.productId] = product.orderQuantity;
        }
      });
      setOrderQuantities(savedQuantities);
    }
  }, [shortages]);

  // Mutation to update order quantity
  const updateOrderQuantityMutation = useMutation({
    mutationFn: async ({ productId, orderQuantity }) => {
      return await api.put(`/orders/stock-shortages/${productId}/order-quantity`, {
        orderQuantity
      });
    },
    onError: (error) => {
      toast.error('Error al guardar cantidad');
      console.error('Error:', error);
    }
  });

  // Mutation to mark product as received
  const markAsReceivedMutation = useMutation({
    mutationFn: async (productId) => {
      return await api.post(`/orders/stock-shortages/${productId}/received`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['stockShortages']);
      queryClient.invalidateQueries(['orders']);
      toast.success('Producto marcado como recibido y pedidos actualizados');
    },
    onError: (error) => {
      toast.error('Error al marcar como recibido');
      console.error('Error:', error);
    },
  });

  const handleMarkAsReceived = (productId) => {
    if (window.confirm('¿Marcar este producto como recibido? Esto actualizará todos los pedidos afectados.')) {
      markAsReceivedMutation.mutate(productId);
    }
  };

  const handleOrderClick = (orderNumber) => {
    navigate('/orders');
  };

  const handleCheckboxChange = (productId, checked) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: checked
    }));
  };

  const handleOrderQuantityChange = (productId, value) => {
    setOrderQuantities(prev => ({
      ...prev,
      [productId]: value
    }));

    // Save to database
    updateOrderQuantityMutation.mutate({ productId, orderQuantity: value });
  };

  const getSelectedCount = () => {
    return Object.values(selectedProducts).filter(Boolean).length;
  };

  const getSelectedProductsData = () => {
    if (!shortages?.products) return [];

    return shortages.products
      .filter(product => selectedProducts[product.productId])
      .map(product => ({
        ...product,
        orderQuantity: orderQuantities[product.productId] || ''
      }));
  };

  const handlePrint = () => {
    window.print();
  };

  // Helper function to determine unit based on product type
  const getProductUnit = (product) => {
    const name = product.productName?.toLowerCase() || '';
    const type = product.type?.toLowerCase() || '';

    // Passepartouts, cristales, traseras use square meters
    if (type.includes('passepartout') || name.includes('passepartout') ||
        type.includes('cristal') || name.includes('cristal') ||
        type.includes('trasera') || name.includes('trasera')) {
      return 'm²';
    }

    // Molduras and everything else use units
    return 'uds';
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingState>Cargando faltas de stock...</LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon>⚠️</EmptyIcon>
          <EmptyTitle>Error al cargar</EmptyTitle>
          <EmptyText>No se pudieron cargar las faltas de stock</EmptyText>
        </EmptyState>
      </Container>
    );
  }

  const totalProducts = shortages?.products?.length || 0;
  const totalOrders = shortages?.stats?.totalOrders || 0;
  const totalComponents = shortages?.stats?.totalComponents || 0;

  if (totalProducts === 0) {
    return (
      <Container>
        <Header>
          <Title>
            <FaBox /> Faltas de Stock
          </Title>
        </Header>
        <EmptyState>
          <EmptyIcon>✅</EmptyIcon>
          <EmptyTitle>¡Todo en stock!</EmptyTitle>
          <EmptyText>No hay componentes marcados como sin stock en este momento.</EmptyText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FaBox /> Faltas de Stock
        </Title>
        <HeaderActions>
          <ViewSelectedButton
            onClick={() => setShowSelectedModal(true)}
            disabled={getSelectedCount() === 0}
          >
            <FaShoppingCart />
            Ver Seleccionados
            {getSelectedCount() > 0 && (
              <SelectedCount>{getSelectedCount()}</SelectedCount>
            )}
          </ViewSelectedButton>
        </HeaderActions>
      </Header>

      <StatsContainer>
        <StatCard $color="#dc3545">
          <StatLabel>Productos Sin Stock</StatLabel>
          <StatValue>{totalProducts}</StatValue>
        </StatCard>
        <StatCard $color="#ff9800">
          <StatLabel>Pedidos Afectados</StatLabel>
          <StatValue>{totalOrders}</StatValue>
        </StatCard>
        <StatCard $color="#2196f3">
          <StatLabel>Componentes Totales</StatLabel>
          <StatValue>{totalComponents}</StatValue>
        </StatCard>
      </StatsContainer>

      <ProductsGrid>
        {shortages.products.map((product) => {
          const unit = getProductUnit(product);
          const isSelected = selectedProducts[product.productId] || false;

          return (
            <ProductCard key={product.productId} $selected={isSelected}>
              <ProductHeader>
                <MobileHeaderRow>
                  <Checkbox
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleCheckboxChange(product.productId, e.target.checked)}
                  />
                  <ProductInfo>
                    <ProductName>
                      <FaExclamationTriangle color="#dc3545" />
                      {product.productName}
                    </ProductName>
                    <ProductType>
                      {product.orders.length} pedido(s) • {product.totalQuantity} {unit}
                    </ProductType>
                  </ProductInfo>
                </MobileHeaderRow>
                <MobileActionsRow>
                  <OrderInputGroup>
                    <InputLabel>
                      {unit === 'm²' ? 'm² a pedir' : 'Uds. a pedir'}
                    </InputLabel>
                    <OrderInput
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={orderQuantities[product.productId] || ''}
                      onChange={(e) => handleOrderQuantityChange(product.productId, e.target.value)}
                    />
                  </OrderInputGroup>
                  <ProductActions>
                    <MarkReceivedButton
                      onClick={() => handleMarkAsReceived(product.productId)}
                      disabled={markAsReceivedMutation.isLoading}
                    >
                      <FaCheckCircle />
                      Recibido
                    </MarkReceivedButton>
                  </ProductActions>
                </MobileActionsRow>
              </ProductHeader>

              <OrdersList>
                {product.orders.map((order) => (
                  <OrderItem key={`${order.orderNumber}-${order.lineId}`}>
                    <OrderItemRow>
                      <OrderNumber onClick={() => handleOrderClick(order.orderNumber)}>
                        #{order.orderNumber}
                      </OrderNumber>
                      <OrderClient>{order.clientName}</OrderClient>
                    </OrderItemRow>
                    <OrderItemRow>
                      <OrderDimensions>
                        {order.dimensions}
                      </OrderDimensions>
                      <OrderQuantity>
                        {order.requiredQuantity ? order.requiredQuantity.toFixed(2) : order.quantity} {unit}
                      </OrderQuantity>
                      <OrderUnit>
                        {order.quantity > 1 ? `×${order.quantity}` : ''}
                      </OrderUnit>
                    </OrderItemRow>
                  </OrderItem>
                ))}
              </OrdersList>
            </ProductCard>
          );
        })}
      </ProductsGrid>

      {/* Compact Selected Products Modal */}
      {showSelectedModal && (
        <Modal onClick={() => setShowSelectedModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <PrintHeader>
              <h1>Pedido de Productos</h1>
              <p>Fecha: {new Date().toLocaleDateString('es-ES')}</p>
            </PrintHeader>

            <ModalHeader>
              <ModalTitle>
                <FaShoppingCart />
                Pedido a Proveedor
              </ModalTitle>
              <ModalActions>
                <PrintButton onClick={handlePrint}>
                  <FaPrint />
                  Imprimir
                </PrintButton>
                <CloseButton onClick={() => setShowSelectedModal(false)}>
                  <FaTimes />
                </CloseButton>
              </ModalActions>
            </ModalHeader>

            <CompactList>
              {getSelectedProductsData().map((product) => {
                const unit = getProductUnit(product);
                const quantity = product.orderQuantity || product.totalQuantity;
                return (
                  <CompactItem key={product.productId}>
                    <CompactName>{product.productName}</CompactName>
                    <CompactQuantity>
                      {quantity} {unit}
                    </CompactQuantity>
                  </CompactItem>
                );
              })}
            </CompactList>

            {getSelectedProductsData().length === 0 && (
              <EmptyText style={{ textAlign: 'center', padding: '2rem' }}>
                No hay productos seleccionados
              </EmptyText>
            )}

            <TotalRow>
              <span>Total de productos:</span>
              <span>{getSelectedProductsData().length}</span>
            </TotalRow>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default StockShortages;
